import "./FlaggingModal.css";
import { Field, Form, Formik } from "formik";
import { useState } from "react";
import AutoSuggest from "react-autosuggest";
import ReactModal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { SlideFlagPOST, Stain, User } from "pwapi";

import { StainList } from "custom-models/frontLoader";

import { caseSearchToURL } from "shared/actions/casesActions";
import { createFlag, createMislabeledFlag } from "shared/actions/flagActions";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import selectLabStains from "shared/selectors/selectLabStains";
import { selectSortedCaseDetailSlides } from "shared/selectors/selectSortedCaseDetail";
import { getNextCaseId } from "shared/utils/caseUtils";
import { splitFlagReason } from "shared/utils/labOrderUtils";
import { getWrapAroundSlide, slideDetailLabel } from "shared/utils/slideUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import SelectInput from "components/customInputs/SelectInput";
import TextareaInput from "components/customInputs/TextareaInput";
import ApplicationState from "store/ApplicationState";
import { useAppSelector } from "store/hooks";

type FlaggingModalProps = {
  caseDetail: CaseDetailState;
  slideDetail: SlideDetailState;
  isOpen: boolean;
  setIsOpen: Function;
};
type StainRequest = "rescan" | "recut" | "repeat" | "reembed" | "";
type FlagForm = {
  request: StainRequest;
  note?: string;
  reason: string;
  stain?: string;
};

const getReasonCatalog = (stain?: Stain) => {
  const heslide = stain?.abbreviation === "H&E" ? "recut" : "repeat";

  const reasonCatalog: {
    [key: string]: {
      requestList?: string[];
      canChooseStain?: boolean;
      initialStain?: string;
    };
  } = {
    MISSING_EPIDERMIS: { canChooseStain: true, initialStain: "DEEPEP" },
    EMBEDDED_INCORRECTLY: { requestList: ["reembed"] },
    TANGENTIAL_SECTION: { canChooseStain: true },
    MISLABELED: {},
    DATA_ENTRY_ERROR: { canChooseStain: true },
    INCORRECT_ORDER: { requestList: [heslide] },
    BLURRY: { requestList: ["rescan"] },
    TILING: { requestList: ["rescan"] },
    SCAN_MISSING_TISSUE: { requestList: ["rescan"] },
    // INCOMPLETE_SCAN: { requestList: ["rescan"] },  # Removed 11/08/22
    FOLDED_TISSUE: { canChooseStain: true },
    TISSUE_OUT_OF_SCAN_FIELD: {
      requestList: [heslide],
    },
    AIR_BUBBLE: { requestList: ["rescan"] },
    NO_COVERSLIP: { requestList: ["rescan"] },
    OTHER: {
      requestList: [heslide, "reembed", "rescan"],
      canChooseStain: true,
    },
  };

  return reasonCatalog;
};

const FlaggingModal = ({
  caseDetail,
  slideDetail,
  isOpen,
  setIsOpen,
}: FlaggingModalProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<
    ReturnType<typeof selectLabStains>
  >([]);
  const stains = useAppSelector(({ stains }) => stains);
  const labStains = useAppSelector((state) =>
    selectLabStains(state, caseDetail.accessioning_lab?.id || 0, true)
  );
  const slideStain = stains[slideDetail.stain_id || 0];
  const user: User | undefined = useSelector(
    ({ auth }: ApplicationState) => auth.user
  );

  const {
    currentCaseSearch: searchParams,
    caseIdList,
    caseIdListPending,
  } = useSelector(({ cases }: ApplicationState) => cases);

  const sortedSlidesList = useAppSelector(selectSortedCaseDetailSlides);

  const findStain = (stain: string = "") =>
    Object.values(stains).find(
      (s) => s.abbreviation.toUpperCase() === stain.toUpperCase()
    );

  const getNextCaseUrl = (): string => {
    const nextCaseId = getNextCaseId(
      caseDetail.id,
      caseIdListPending,
      caseIdList
    );

    return nextCaseId
      ? `/cases/${nextCaseId}`
      : caseSearchToURL(searchParams, user);
  };

  const getNextSlideUrl = (): string => {
    const nextSlide = getWrapAroundSlide(sortedSlidesList, slideDetail);
    return nextSlide
      ? `/cases/${caseDetail.id}/slides/${nextSlide.id}`
      : getNextCaseUrl();
  };

  const getStain = (
    request?: StainRequest,
    stain?: string
  ): StainList | undefined => {
    // If stain Provided, Find and return it.
    if (!!stain) return findStain(stain);

    // No Request and No Stain.  ABORT.
    if (!request) return;

    // If Request has been mapped, retrieve abbr and find stain. Else, return the stain attached to slide.
    const mapRequestToStainAbbr: Partial<Record<StainRequest, string>> = {
      reembed: "REMBD",
      recut: "RCT1",
    };
    return request in mapRequestToStainAbbr
      ? findStain(mapRequestToStainAbbr[request])
      : slideStain;
  };

  const handleOnSubmit = (values: FlagForm) => {
    const stain = getStain(values.request, values.stain);

    const form = {
      slide_id: slideDetail.id || 0,
      reason:
        SlideFlagPOST.reason[
          values.reason
            .toUpperCase()
            .replace(/-/g, "_") as keyof typeof SlideFlagPOST.reason
        ],
      note: values.note,
      is_rescan: values.request === "rescan",
      stain_id: stain?.id,
    };

    dispatch(
      values.reason === "mislabeled"
        ? createMislabeledFlag({
            form: form,
            closeModal: setIsOpen,
            url:
              caseDetail.slides.length > 1
                ? getNextSlideUrl()
                : getNextCaseUrl(),
            navigate: navigate,
          })
        : createFlag({
            form: form,
            closeModal: setIsOpen,
          })
    );
  };

  const onFilterStains = (value: string) =>
    labStains.filter(
      (s) =>
        s.stain?.category === Stain.category.TECH &&
        (s.stain?.name.toUpperCase().startsWith(value.toUpperCase()) ||
          s.stain?.abbreviation.startsWith(value.toUpperCase()))
    );

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={() => setIsOpen(false)}
      className="Modal"
      overlayClassName="Overlay"
    >
      <Formik
        initialValues={{
          reason: "",
          request: "",
          note: undefined,
          stain: "",
        }}
        enableReinitialize
        onSubmit={handleOnSubmit}
      >
        {({ values }) => {
          const reasonCatalog = getReasonCatalog(slideStain as Stain);
          const reasonAttributes =
            reasonCatalog[values.reason.toUpperCase().replace(/-/g, "_")];
          return (
            <Form className="FlaggingModal">
              <span className="modal__header" data-testid="header">
                Flagging{" "}
                <strong>
                  {slideDetailLabel(slideDetail, slideStain, true)}
                </strong>
              </span>
              <div className="modal__select">
                <Field name="reason">
                  {({ field, form }: any) => (
                    <SelectInput
                      label="Reason"
                      id="reason"
                      required
                      options={Object.values(SlideFlagPOST.reason)
                        .filter((r) => r !== "incomplete-scan")
                        .map((p) => ({
                          label: splitFlagReason(p),
                          value: p,
                        }))}
                      {...field}
                      onChange={(e) => {
                        form.setValues({
                          reason: e.target.value,
                          request:
                            e.target.value === "blurry" ||
                            e.target.value === "tiling" ||
                            e.target.value === "air-bubble" ||
                            e.target.value === "no-coverslip" ||
                            e.target.value === "scan-missing-tissue"
                              ? "rescan"
                              : "",
                          stain:
                            reasonCatalog[
                              e.target.value.toUpperCase().replace(/-/g, "_")
                            ].initialStain || "",
                          note: form.values.note,
                        });
                      }}
                    />
                  )}
                </Field>
              </div>
              {values.reason && (
                <div className="modal__multifield">
                  {reasonAttributes?.requestList && (
                    <Field name="request">
                      {({ field, form }: any) => {
                        return (
                          <div className="checkbox__container">
                            {reasonAttributes?.requestList &&
                              reasonAttributes.requestList.map(
                                (val: string) => {
                                  return (
                                    <label key={val}>
                                      <input
                                        type="checkbox"
                                        checked={
                                          !form.values.stain &&
                                          field.value === val
                                        }
                                        onChange={() => {
                                          form.setValues({
                                            ...form.values,
                                            request:
                                              field.value === val ? "" : val,
                                            stain: "",
                                          });
                                        }}
                                      />
                                      {val === "repeat" ? "Repeat Order" : val}
                                    </label>
                                  );
                                }
                              )}
                          </div>
                        );
                      }}
                    </Field>
                  )}
                  {reasonAttributes?.canChooseStain && (
                    <>
                      {reasonAttributes?.requestList && <span>OR</span>}
                      <CustomInput id="TECH" label="Lab Order (Optional)">
                        <Field name="stain">
                          {({ field, form }: any) => (
                            <AutoSuggest
                              suggestions={suggestions}
                              getSuggestionValue={(suggestion) =>
                                suggestion.stain?.abbreviation || ""
                              }
                              inputProps={{
                                id: "TECH",
                                name: "TECH",
                                alt: "newStain__input",
                                value: field.value,
                                onChange: (_, { newValue }) => {
                                  form.setValues({
                                    ...form.values,
                                    stain: newValue,
                                    request: "",
                                  });
                                },
                              }}
                              onSuggestionsFetchRequested={({ value }) => {
                                const filtered = onFilterStains(value);
                                setSuggestions(filtered);
                              }}
                              onSuggestionsClearRequested={() => {
                                setSuggestions([]);
                              }}
                              renderSuggestion={(s) => (
                                <div>
                                  <strong>{s.stain?.abbreviation}</strong> (
                                  {s.stain?.name})
                                </div>
                              )}
                            />
                          )}
                        </Field>
                      </CustomInput>
                    </>
                  )}
                </div>
              )}
              <Field name="note">
                {({ field, form }: any) => (
                  <TextareaInput
                    label="Additional Details"
                    id="additional_details"
                    required={form.values.reason === "other"}
                    {...field}
                  />
                )}
              </Field>
              <div className="button__container">
                <PathologyWatchButton type="submit">Flag</PathologyWatchButton>
              </div>
            </Form>
          );
        }}
      </Formik>
    </ReactModal>
  );
};

export default FlaggingModal;
